import {useCallback, useEffect, useRef} from "react";
import {useLocation, useParams} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";

import type {RootState} from "../../../store";

import {load as loadConstructions} from "../../constructionsNew/constructionsNewSlice";
import {load as loadCurrentConstruction} from "../../constructionNew/constructionNewSlice";
import {load as loadCalculation} from "../../constructionCalculationsNew/constructionCalculationsNewSlice";

import PathConstants from "../../../routes/pathConstants";
import useConstructionCalculationsFetchingHook from "./useConstructionCalculationsFetchingHook";
import type CalculationListItemDto from "../../../dto/calculations/CalculationListItemDto";
import type CalculationResultDto from "../../../dto/calculations/CalculationResultDto";
import type CalculationNoteMatGroundType from "../../../dto/calculations/CalculationNoteMatGroundTypeDto";
import type CalculationConstructionDetailsDto from "../../../dto/calculations/CalculationConstructionDetailsDto";

/** Single responsibility: orchestrate all fetches and dispatch loaded page data to Redux. */
const useConstructionCalculationsLoaderHook = (): {
  loadPageData: (selectedCalculationId?: string | null) => Promise<void>;
} => {
  const {id} = useParams();
  const {pathname} = useLocation();
  const dispatch = useDispatch();
  const {all} = useSelector((state: RootState) => state.constructionsNew);
  const {nextId, prevId} = useSelector((state: RootState) => state.constructionNew);

  // Use refs so loadPageData always reads the latest values without recreating on each store update
  const allRef = useRef(all);
  const nextIdRef = useRef(nextId);
  const prevIdRef = useRef(prevId);
  useEffect(() => {
    allRef.current = all;
    nextIdRef.current = nextId;
    prevIdRef.current = prevId;
  }, [all, nextId, prevId]);

  const {
    getConstructions,
    getCurrentConstruction,
    getCalculations,
    getCalculationProfiles,
    getNextPreviousCalculationIds,
    getConstructionDetails,
    getCalculationNoteMatGroundTypesById,
  } = useConstructionCalculationsFetchingHook();

  const loadPageData = useCallback(
    async (selectedCalculationId?: string | null): Promise<void> => {
      const constructions = await getConstructions();
      const currentConstruction = await getCurrentConstruction();
      const lclCalculations = await getCalculations();

      let lclSelectedCalculation: CalculationListItemDto | undefined;

      let lclCurrentCalculationResults: CalculationResultDto | undefined;
      let lclConstructionDetails: CalculationConstructionDetailsDto | undefined;
      let lclCurrentMastNo: string | undefined;
      let matGroundTypes: CalculationNoteMatGroundType[] | undefined;

      if (lclCalculations !== undefined && lclCalculations.length > 0) {
        lclSelectedCalculation =
          lclCalculations.find(c => c.calculationId === selectedCalculationId) ?? lclCalculations[0];

        const shouldLoadMatGroundTypes = pathname?.includes(PathConstants.CalculationsNote);
        if (shouldLoadMatGroundTypes) {
          const matGroundTypesResponse = await getCalculationNoteMatGroundTypesById(
            lclSelectedCalculation?.calculationId,
          );
          matGroundTypes = matGroundTypesResponse.data ?? undefined;
        }

        if (lclSelectedCalculation !== undefined && lclSelectedCalculation !== null) {
          lclSelectedCalculation.selected = true;
          lclCurrentCalculationResults = (await getCalculationProfiles(lclSelectedCalculation)) ?? undefined;

          const constructionDetailsResponse = await getConstructionDetails(lclSelectedCalculation.calculationId);
          lclConstructionDetails = constructionDetailsResponse.data?.calculationConstructionDetails ?? undefined;
          lclCurrentMastNo =
            lclCurrentCalculationResults?.mastResults !== undefined &&
            lclCurrentCalculationResults?.mastResults.length > 0
              ? lclCurrentCalculationResults.mastResults[0]?.mastNo
              : undefined;
        }
      }

      const nextCalculationId = await getNextPreviousCalculationIds(nextIdRef.current);
      const prevCalculationId = await getNextPreviousCalculationIds(prevIdRef.current);

      if (id !== undefined) {
        if (allRef.current.length <= 0) {
          dispatch(loadConstructions({data: constructions}));
        }
        dispatch(
          loadCurrentConstruction({
            currentConstruction: currentConstruction ? { ...currentConstruction, matGroundTypes } : null,
            latestCalculationId: lclSelectedCalculation?.calculationId,
            all: allRef.current.length > 0 ? allRef.current : constructions,
            defaultCalculationParams: null,
          }),
        );
        dispatch(
          loadCalculation({
            currentCalculationId: lclSelectedCalculation?.calculationId,
            calculations: lclCalculations,
            constructionDetails: lclConstructionDetails,
            currentCalculationResults: lclCurrentCalculationResults,
            currentMastNo: lclCurrentMastNo,
            nextId: nextCalculationId,
            prevId: prevCalculationId,
          }),
        );
      }
    },
    [
      dispatch,
      getCalculationNoteMatGroundTypesById,
      getCalculationProfiles,
      getCalculations,
      getConstructionDetails,
      getConstructions,
      getCurrentConstruction,
      getNextPreviousCalculationIds,
      id,
      pathname,
    ],
  );

  return {loadPageData};
};

export default useConstructionCalculationsLoaderHook;
